import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTracking, clearTrackingData } from '../../redux/slices/orderSlice';
import {
  MapPin, Clock, Calendar, CheckCircle, Circle,
  Package, RefreshCw, AlertCircle, Navigation,
  ChevronDown, ChevronUp,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  ROAD_W:      20,
  ROAD_COL:    56,
  V_PAD:       76,
  V_GAP:       116,
  N:           5,

  H_ROAD_H:    14,
  H_PAD_L:     80,
  H_PAD_R:     80,
  H_TRUCK_GAP: 10,

  CARD_RADIUS:  2,   // sharp — VESHH-EON
  CARD_PAD_V:   9,
  CARD_PAD_H:   11,

  BLACK:   '#0a0a0a',
  GREY_1:  '#f6f6f6',
  GREY_2:  '#e8e8e8',
  GREY_3:  '#c0c0c0',
  GREY_4:  '#999',
  GREY_5:  '#666',
  WHITE:   '#ffffff',
  FONT:    "'Exo 2', 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

T.V_ROAD_H = T.V_PAD + (T.N - 1) * T.V_GAP + T.V_PAD;

const TRUCK_URL =
  'https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/Artboard_3_x89zi6';

const TK_W = 40;
const TK_H = Math.round(TK_W * (1350 / 1080));

const STAGES = [
  { key: 'Booked',           label: 'Order Booked'     },
  { key: 'Processing',       label: 'Processing'       },
  { key: 'In Transit',       label: 'In Transit'       },
  { key: 'Out for Delivery', label: 'Out for Delivery' },
  { key: 'Delivered',        label: 'Delivered'        },
];

// Contextual messages shown when a stage is current — reassures customer
const STAGE_CONTEXT = {
  'Booked':           'Your order has been confirmed and handed to the courier.',
  'Processing':       'The courier is preparing your shipment for dispatch.',
  'In Transit':       'Moving between hubs — scan updates appear at each checkpoint.',
  'Out for Delivery': 'Your package is with the delivery agent today.',
  'Delivered':        'Package delivered successfully.',
};

const STATUS_MAP = {
  'booked': 'Booked', 'order booked': 'Booked',
  'pick up': 'Booked', 'picked up': 'Booked',
  'pickup': 'Booked', 'pick up scan': 'Booked',
  'manifested': 'Processing', 'processing': 'Processing',
  'in transit': 'In Transit', 'intransit': 'In Transit',
  'transit': 'In Transit', 'dispatched': 'In Transit',
  'shipped': 'In Transit', 'arrival at': 'In Transit',
  'out for delivery': 'Out for Delivery', 'out-for-delivery': 'Out for Delivery',
  'with delivery': 'Out for Delivery', 'delivery attempt': 'Out for Delivery',
  'delivered': 'Delivered', 'delivery done': 'Delivered',
  'shipment delivered': 'Delivered',
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function norm(raw = '') {
  const s = raw.toLowerCase().trim();
  if (STATUS_MAP[s]) return STATUS_MAP[s];
  for (const [k, v] of Object.entries(STATUS_MAP)) if (s.includes(k)) return v;
  return raw;
}

function stageIdx(status) {
  const n = norm(status);
  const i = STAGES.findIndex(s => s.key.toLowerCase() === n.toLowerCase());
  return i === -1 ? 0 : i;
}

function vTop(i) { return T.V_PAD + i * T.V_GAP; }

function fmtDate(raw) {
  if (!raw) return null;
  try {
    const d = new Date(String(raw).trim());
    if (isNaN(d)) return String(raw);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  } catch { return String(raw); }
}

function fmtTime(raw) {
  if (!raw) return null;
  try {
    const d = new Date(String(raw).trim());
    if (isNaN(d)) return null;
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return null; }
}

// Returns how many days since a date (0 = today, 1 = yesterday, etc.)
function daysSince(raw) {
  if (!raw) return null;
  try {
    const d = new Date(String(raw).trim());
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  } catch { return null; }
}

function findActivity(activities, key) {
  if (!activities?.length) return null;
  return [...activities].reverse().find(a => {
    const t = `${a.status || ''} ${a.activity || ''}`.toLowerCase();
    return norm(t.split('\n')[0].trim()) === key;
  }) || null;
}

function fallbackDate(key, od) {
  if (!od) return null;
  return (
    key === 'Booked'           ? od.createdAt   :
    key === 'Processing'       ? od.createdAt   :
    key === 'In Transit'       ? od.shippedAt   :
    key === 'Out for Delivery' ? od.shippedAt   :
    key === 'Delivered'        ? od.deliveredAt : null
  ) || null;
}

function milestoneData(stage, idx, curIdx, activities, od) {
  const done    = idx < curIdx;
  const current = idx === curIdx;
  const future  = idx > curIdx;
  const act     = findActivity(activities, stage.key);
  const rawDate = act?.date || ((done || current) ? fallbackDate(stage.key, od) : null);
  return {
    done, current, future,
    date:       fmtDate(rawDate),
    time:       fmtTime(rawDate),
    location:   act?.location || null,
    eventLabel: act ? (act.activity || act.status || '').trim() : null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────

const S = {
  card: (current, done, future) => ({
    padding:    `${T.CARD_PAD_V}px ${T.CARD_PAD_H}px`,
    borderRadius: T.CARD_RADIUS,
    background: current ? T.BLACK : done ? T.GREY_1 : 'transparent',
    border:     `1.5px ${future ? 'dashed' : 'solid'} ${current ? 'transparent' : done ? T.GREY_2 : '#ddd'}`,
    opacity:    future ? 0.38 : 1,
    transition: 'background 0.35s ease, border-color 0.35s ease, opacity 0.35s ease',
    boxSizing:  'border-box',
    width:      '100%',
    minWidth:   0,
    overflow:   'hidden',
  }),
  metaRow: (rtl) => ({
    display:        'flex',
    alignItems:     'center',
    gap:            3,
    flexDirection:  rtl ? 'row-reverse' : 'row',
    overflow:       'hidden',
    maxWidth:       '100%',
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Keyframe injection (once)
// ─────────────────────────────────────────────────────────────────────────────

const TT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600;700&display=swap');

  @keyframes tt-pulse-ring {
    0%   { transform: translate(-50%,-50%) scale(1);   opacity: 0.55; }
    70%  { transform: translate(-50%,-50%) scale(2.2); opacity: 0; }
    100% { transform: translate(-50%,-50%) scale(2.2); opacity: 0; }
  }
  @keyframes tt-truck-idle {
    0%, 100% { transform: translateX(-50%) translateY(-50%) translateY(0px); }
    50%       { transform: translateX(-50%) translateY(-50%) translateY(-2px); }
  }
  @keyframes tt-truck-idle-h {
    0%, 100% { margin-top: 0px; }
    50%       { margin-top: -2px; }
  }
  @keyframes tt-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes tt-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Card content
// ─────────────────────────────────────────────────────────────────────────────

function CardContent({ stage, done, current, future, date, time, location, eventLabel, align }) {
  const rtl        = align === 'right';
  const center     = align === 'center';
  const labelCol   = current ? T.WHITE  : done ? '#111' : T.GREY_3;
  const metaCol    = current ? 'rgba(255,255,255,0.65)' : T.GREY_4;
  const alignItems = center ? 'center' : rtl ? 'flex-end' : 'flex-start';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems, gap: 4, minWidth: 0, width: '100%', fontFamily: T.FONT }}>

      {/* Stage label + icon */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5,
        flexDirection: rtl ? 'row-reverse' : 'row', minWidth: 0, maxWidth: '100%' }}>
        <div style={{ flexShrink: 0 }}>
          {done
            ? <CheckCircle size={12} color={current ? T.WHITE : '#222'} strokeWidth={2.5} />
            : current
            ? <Navigation  size={12} color={T.WHITE}                    strokeWidth={2.5} />
            : <Circle      size={12} color={T.GREY_3}                   strokeWidth={1.5} />}
        </div>
        <span style={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: '0.15em',
          textTransform: 'uppercase', lineHeight: 1.25,
          color: labelCol, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', minWidth: 0,
        }}>
          {stage.label}
        </span>
      </div>

      {/* Courier event label */}
      {eventLabel && eventLabel.toUpperCase() !== stage.label.toUpperCase() && (
        <span style={{
          fontSize: 9, fontWeight: 400, letterSpacing: '0.08em', lineHeight: 1.3,
          color: metaCol, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', maxWidth: '100%',
          textAlign: center ? 'center' : rtl ? 'right' : 'left',
        }}>
          {eventLabel}
        </span>
      )}

      {/* Contextual message — only on current, active stage */}
      {current && !done && (
        <span style={{
          fontSize: 8.5, fontWeight: 300, letterSpacing: '0.04em',
          lineHeight: 1.5, color: 'rgba(255,255,255,0.5)',
          textAlign: center ? 'center' : rtl ? 'right' : 'left',
          marginTop: 2,
        }}>
          {STAGE_CONTEXT[stage.key]}
        </span>
      )}

      {/* Date */}
      {date && (
        <span style={{ ...S.metaRow(rtl), fontSize: 10, color: metaCol, fontFamily: T.FONT }}>
          <Calendar size={9} strokeWidth={2} color={metaCol} style={{ flexShrink: 0 }} />
          {date}
        </span>
      )}

      {/* Time */}
      {time && (
        <span style={{ ...S.metaRow(rtl), fontSize: 10, color: metaCol, fontFamily: T.FONT }}>
          <Clock size={9} strokeWidth={2} color={metaCol} style={{ flexShrink: 0 }} />
          {time}
        </span>
      )}

      {/* Location */}
      {location && (
        <span style={{ ...S.metaRow(rtl), fontSize: 9.5, color: metaCol, fontFamily: T.FONT }}>
          <MapPin size={9} strokeWidth={2} color={metaCol} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {location}
          </span>
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Road dot — with pulse ring on current
// ─────────────────────────────────────────────────────────────────────────────

function RoadDot({ done, current, style = {} }) {
  const sz = current ? 18 : done ? 14 : 11;
  return (
    <div style={{ position: 'relative', width: sz, height: sz, ...style }}>
      {/* Pulse ring — only on current */}
      {current && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: sz, height: sz, borderRadius: '50%',
          border: `2px solid ${T.BLACK}`,
          animation: 'tt-pulse-ring 2s ease-out infinite',
        }} />
      )}
      <div style={{
        width: sz, height: sz, borderRadius: '50%', flexShrink: 0,
        background: done || current ? T.BLACK : T.WHITE,
        border: `2.5px solid ${done || current ? T.BLACK : T.GREY_3}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.4s ease',
        position: 'relative', zIndex: 1,
      }}>
        {done && (
          <svg width="7" height="7" viewBox="0 0 8 8">
            <polyline points="1,4 3,6.5 7,1.5"
              stroke="#fff" strokeWidth="2" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {current && !done && (
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.WHITE }} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE LAYOUT
// ─────────────────────────────────────────────────────────────────────────────

function MobileLayout({ currentStageIdx, activities, orderDates, vFillH, displayTopPx, animating }) {
  const GAP = 10;

  return (
    <div style={{
      display: 'flex', flexDirection: 'row', alignItems: 'stretch',
      padding: '0 16px', gap: GAP, position: 'relative',
      height: T.V_ROAD_H, boxSizing: 'border-box',
    }}>

      {/* Left card column */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {STAGES.map((stage, idx) => {
          if (idx % 2 !== 0) return null;
          const d = milestoneData(stage, idx, currentStageIdx, activities, orderDates);
          return (
            <div key={stage.key} style={{
              position: 'absolute', top: vTop(idx), left: 0, right: 0,
              transform: 'translateY(-50%)',
            }}>
              <div style={S.card(d.current, d.done, d.future)}>
                <CardContent stage={stage} {...d} align="left" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Road column */}
      <div style={{ width: T.ROAD_COL, flexShrink: 0, position: 'relative' }}>

        {/* Left rail */}
        <div style={{
          position: 'absolute', top: T.V_PAD, bottom: T.V_PAD,
          left: `calc(50% - ${T.ROAD_W / 2 + 1}px)`, width: 2,
          background: T.BLACK,
        }} />

        {/* Right rail */}
        <div style={{
          position: 'absolute', top: T.V_PAD, bottom: T.V_PAD,
          left: `calc(50% + ${T.ROAD_W / 2 - 1}px)`, width: 2,
          background: T.BLACK,
        }} />

        {/* Grey dotted centre — unpaved */}
        <div style={{
          position: 'absolute', top: T.V_PAD, bottom: T.V_PAD,
          left: 'calc(50% - 1px)', width: 2, zIndex: 1,
          backgroundImage: 'repeating-linear-gradient(to bottom,#aaa 0,#aaa 5px,transparent 5px,transparent 13px)',
        }} />

        {/* Black fill — paved */}
        <div style={{
          position: 'absolute', top: T.V_PAD,
          left: `calc(50% - ${T.ROAD_W / 2}px)`,
          width: T.ROAD_W, height: vFillH,
          background: T.BLACK, zIndex: 2,
          transition: animating ? 'height 1.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
        }} />

        {/* White dotted centre — paved */}
        <div style={{
          position: 'absolute', top: T.V_PAD,
          left: 'calc(50% - 1px)', width: 2,
          height: vFillH, zIndex: 3,
          backgroundImage: 'repeating-linear-gradient(to bottom,#fff 0,#fff 5px,transparent 5px,transparent 13px)',
          transition: animating ? 'height 1.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
        }} />

        {/* Milestone dots */}
        {STAGES.map((stage, idx) => (
          <div key={stage.key} style={{
            position: 'absolute', top: vTop(idx), left: '50%',
            transform: 'translate(-50%, -50%)', zIndex: 8,
          }}>
            <RoadDot done={idx < currentStageIdx} current={idx === currentStageIdx} />
          </div>
        ))}

        {/* Truck — idle bounce when not animating */}
        <img src={TRUCK_URL} alt="truck" style={{
          position: 'absolute',
          top: displayTopPx, left: '50%',
          transform: 'translateX(-50%) translateY(-50%)',
          width: TK_W, height: TK_H,
          objectFit: 'contain',
          filter: 'grayscale(100%) contrast(1.1)',
          zIndex: 10,
          transition: animating ? 'top 1.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
          animation: !animating ? 'tt-truck-idle 2.4s ease-in-out infinite' : 'none',
        }} />
      </div>

      {/* Right card column */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {STAGES.map((stage, idx) => {
          if (idx % 2 !== 1) return null;
          const d = milestoneData(stage, idx, currentStageIdx, activities, orderDates);
          return (
            <div key={stage.key} style={{
              position: 'absolute', top: vTop(idx), left: 0, right: 0,
              transform: 'translateY(-50%)',
            }}>
              <div style={S.card(d.current, d.done, d.future)}>
                <CardContent stage={stage} {...d} align="right" />
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP LAYOUT
// ─────────────────────────────────────────────────────────────────────────────

function DesktopLayout({ currentStageIdx, activities, orderDates, displayLeftPct, animating }) {
  const ABOVE  = 72;
  const RH     = 14;
  const BELOW  = 14;
  const CARD_H = 100;
  const TOTAL  = ABOVE + RH + BELOW + CARD_H + 20;
  const HPAD   = 5;

  const roadCY = ABOVE + RH / 2;

  const visW = TK_H;
  const visH = TK_W;

  const fillWidthPct = Math.max(0, displayLeftPct - HPAD);

  return (
    <div style={{ padding: '0 24px' }}>
      <div style={{ position: 'relative', height: TOTAL }}>

        {/* Road top rail */}
        <div style={{
          position: 'absolute', top: ABOVE, height: 2,
          left: `${HPAD}%`, right: `${HPAD}%`, background: T.BLACK,
        }} />

        {/* Road bottom rail */}
        <div style={{
          position: 'absolute', top: ABOVE + RH - 2, height: 2,
          left: `${HPAD}%`, right: `${HPAD}%`, background: T.BLACK,
        }} />

        {/* Grey dotted centre */}
        <div style={{
          position: 'absolute', top: roadCY - 1, height: 2,
          left: `${HPAD}%`, right: `${HPAD}%`, zIndex: 1,
          backgroundImage: 'repeating-linear-gradient(to right,#aaa 0,#aaa 7px,transparent 7px,transparent 16px)',
        }} />

        {/* Black progress fill */}
        <div style={{
          position: 'absolute', top: ABOVE, height: RH,
          left: `${HPAD}%`, width: `${fillWidthPct}%`,
          background: T.BLACK, zIndex: 2,
          transition: animating ? 'width 1.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
        }} />

        {/* White dotted centre — paved */}
        <div style={{
          position: 'absolute', top: roadCY - 1, height: 2,
          left: `${HPAD}%`, width: `${fillWidthPct}%`, zIndex: 3,
          backgroundImage: 'repeating-linear-gradient(to right,#fff 0,#fff 7px,transparent 7px,transparent 16px)',
          transition: animating ? 'width 1.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
        }} />

        {/* Milestone dots */}
        {STAGES.map((stage, idx) => {
          const leftPct = HPAD + (idx / (T.N - 1)) * (100 - 2 * HPAD);
          return (
            <div key={stage.key} style={{
              position: 'absolute', left: `${leftPct}%`, top: roadCY,
              transform: 'translate(-50%, -50%)', zIndex: 8,
            }}>
              <RoadDot done={idx < currentStageIdx} current={idx === currentStageIdx} />
            </div>
          );
        })}

        {/* Truck wrapper — idle bounce when static */}
        <div style={{
          position: 'absolute',
          left: `${displayLeftPct}%`,
          top: roadCY - visH - T.H_TRUCK_GAP,
          transform: 'translateX(-50%)',
          width: visW, height: visH,
          zIndex: 10,
          transition: animating ? 'left 1.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
          overflow: 'visible',
          animation: !animating ? 'tt-truck-idle-h 2.4s ease-in-out infinite' : 'none',
        }}>
          <img src={TRUCK_URL} alt="truck" style={{
            position: 'absolute',
            top: '50%', left: '50%',
            width: TK_H, height: TK_W,
            transform: 'translate(-50%,-50%) rotate(-90deg)',
            transformOrigin: 'center center',
            objectFit: 'contain',
            filter: 'grayscale(100%) contrast(1.1)',
          }} />
        </div>

        {/* Cards row */}
        <div style={{
          position: 'absolute',
          top: ABOVE + RH + BELOW,
          left: `${HPAD}%`, right: `${HPAD}%`,
          display: 'flex', flexDirection: 'row',
        }}>
          {STAGES.map((stage, idx) => {
            const d = milestoneData(stage, idx, currentStageIdx, activities, orderDates);
            const align = idx === 0 ? 'left' : idx === T.N - 1 ? 'right' : 'center';
            return (
              <div key={stage.key} style={{
                flex: 1, minWidth: 0, display: 'flex',
                justifyContent: idx === 0 ? 'flex-start' : idx === T.N - 1 ? 'flex-end' : 'center',
                paddingRight: idx === T.N - 1 ? 4 : 0,
                paddingLeft:  idx === 0        ? 4 : 0,
              }}>
                <div style={{
                  ...S.card(d.current, d.done, d.future),
                  width: 'auto', maxWidth: 'calc(100% - 8px)', minWidth: 0, textAlign: align,
                }}>
                  <CardContent stage={stage} {...d} align={align} />
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Scan log row
// ─────────────────────────────────────────────────────────────────────────────

function LogRow({ activity, isLatest }) {
  const d = fmtDate(activity.date);
  const t = fmtTime(activity.date);
  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${T.GREY_2}` }}>
      <div style={{ paddingTop: 5, flexShrink: 0 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: isLatest ? T.BLACK : T.GREY_3 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0, fontFamily: T.FONT }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: isLatest ? 600 : 400,
          color: isLatest ? '#111' : T.GREY_5, letterSpacing: '0.03em' }}>
          {activity.activity || activity.status || ''}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 10px', marginTop: 3 }}>
          {d && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: T.GREY_4, fontFamily: T.FONT }}>
              <Calendar size={9} strokeWidth={2} /> {d}
            </span>
          )}
          {t && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: T.GREY_4, fontFamily: T.FONT }}>
              <Clock size={9} strokeWidth={2} /> {t}
            </span>
          )}
          {activity.location && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: T.GREY_4, fontFamily: T.FONT }}>
              <MapPin size={9} strokeWidth={2} /> {activity.location}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton
// ─────────────────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div style={{ border: `1.5px solid ${T.GREY_2}`, overflow: 'hidden', background: T.WHITE }}>
      <div style={{ background: '#111', height: 70 }} />
      <div style={{ padding: '20px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ width: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 26, paddingTop: 4 }}>
          {[0,1,2,3,4].map(i => (
            <div key={i} style={{ width: 12, height: 12, borderRadius: '50%', background: T.GREY_1, flexShrink: 0 }} />
          ))}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {[0,1,2,3,4].map(i => (
            <div key={i} style={{
              height: 44, borderRadius: 2,
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              backgroundSize: '200% 100%',
              animation: 'tt-shimmer 1.5s infinite',
              opacity: 0.7 - i * 0.1,
            }} />
          ))}
        </div>
      </div>
      <style>{`@keyframes tt-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stale indicator — shown when last update was >1 day ago
// ─────────────────────────────────────────────────────────────────────────────

function StaleNotice({ lastUpdated, currentStageKey }) {
  const days = daysSince(lastUpdated);
  if (days === null || days < 1) return null;

  // Don't show for delivered orders
  if (currentStageKey === 'Delivered') return null;

  const msg = days === 1
    ? 'Last scan was yesterday.'
    : `Last scan was ${days} days ago.`;

  return (
    <div style={{
      borderTop: `1px solid ${T.GREY_2}`,
      padding: '10px 20px',
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#fafafa', fontFamily: T.FONT,
    }}>
      <Clock size={10} strokeWidth={2.5} color={T.GREY_4} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 10, color: T.GREY_5, letterSpacing: '0.05em' }}>
        {msg} Couriers scan at hubs — your package is still moving.
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────

export default function TrackingTimeline({ awb, courierName, orderStatus, orderDates }) {
  const dispatch = useDispatch();
  const { trackingData, trackingLoading, trackingError } = useSelector(s => s.orders);

  const [showLog,        setShowLog]        = useState(false);
  const [isDesktop,      setIsDesktop]      = useState(false);
  const [displayTopPx,   setDisplayTopPx]   = useState(vTop(0));
  const [displayLeftPct, setDisplayLeftPct] = useState(5);
  const [animating,      setAnimating]      = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!awb) return;
    setDisplayTopPx(vTop(0)); setDisplayLeftPct(5); setAnimating(false);
    dispatch(clearTrackingData());
    dispatch(fetchTracking(awb));
    return () => dispatch(clearTrackingData());
  }, [awb, dispatch]);

  useEffect(() => {
    if (!trackingData) return;
    const si = stageIdx(trackingData?.currentStatus || 'Booked');
    const di = stageIdx(orderStatus || 'Processing');
    const ti = Math.max(si, di);

    const HPAD = 5;
    const targetLeft = HPAD + (ti / (T.N - 1)) * (100 - 2 * HPAD);

    setDisplayTopPx(vTop(0)); setDisplayLeftPct(HPAD); setAnimating(false);
    const t = setTimeout(() => {
      setAnimating(true);
      setDisplayTopPx(vTop(ti));
      setDisplayLeftPct(targetLeft);
    }, 100);
    return () => clearTimeout(t);
  }, [trackingData, orderStatus]);

  const handleRefresh = () => {
    setDisplayTopPx(vTop(0)); setDisplayLeftPct(5); setAnimating(false);
    dispatch(clearTrackingData());
    dispatch(fetchTracking(awb));
  };

  const si            = stageIdx(trackingData?.currentStatus || 'Booked');
  const di            = stageIdx(orderStatus || 'Processing');
  const curIdx        = Math.max(si, di);
  const activities    = trackingData?.activities || [];
  const hasActivities = activities.length > 0;
  const vFillH        = Math.max(0, displayTopPx - T.V_PAD);

  const expectedDate  = fmtDate(trackingData?.expectedDelivery);
  const lastUpdDate   = fmtDate(trackingData?.lastUpdated);
  const lastUpdTime   = fmtTime(trackingData?.lastUpdated);
  const currentStageKey = STAGES[curIdx]?.key;

  // ── Loading / Error / Empty ───────────────────────────────────────────────

  if (trackingLoading) return <><style>{TT_STYLES}</style><Skeleton /></>;

  if (trackingError) return (
    <>
      <style>{TT_STYLES}</style>
      <div style={{
        background: T.WHITE, border: `1.5px solid ${T.GREY_2}`,
        padding: 20, fontFamily: T.FONT,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <AlertCircle size={15} />
          <span style={{ fontWeight: 600, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Tracking unavailable
          </span>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 11, color: T.GREY_4 }}>{trackingError}</p>
        <button onClick={handleRefresh} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '7px 14px', background: T.BLACK,
          color: T.WHITE, border: 'none', cursor: 'pointer',
          fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
          fontFamily: T.FONT,
        }}>
          <RefreshCw size={11} /> Try Again
        </button>
      </div>
    </>
  );

  if (!trackingData) return null;

  // ── Header ────────────────────────────────────────────────────────────────

  const header = (
    <div style={{
      background: T.BLACK, color: T.WHITE,
      padding: isDesktop ? '18px 28px' : '15px 20px',
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'center', flexWrap: 'wrap', gap: 10,
      fontFamily: T.FONT,
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
          <Package size={12} strokeWidth={2.5} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.4em', textTransform: 'uppercase' }}>
            Live Tracking
          </span>
          {/* Live pulse dot */}
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#4ade80',
            display: 'inline-block', marginLeft: 2,
            animation: 'tt-blink 1.8s ease-in-out infinite',
          }} />
        </div>
        <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em' }}>
          {courierName || trackingData?.courier || 'Courier'}&nbsp;·&nbsp;AWB {awb}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        {expectedDate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10 }}>
            <Calendar size={9} strokeWidth={2} color="rgba(255,255,255,0.35)" />
            <span style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em' }}>Expected</span>
            <span style={{ color: T.WHITE, fontWeight: 700 }}>{expectedDate}</span>
          </div>
        )}
        {(lastUpdDate || lastUpdTime) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10 }}>
            <Clock size={9} strokeWidth={2} color="rgba(255,255,255,0.35)" />
            <span style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em' }}>Updated</span>
            {lastUpdDate && <span style={{ color: 'rgba(255,255,255,0.75)' }}>{lastUpdDate}</span>}
            {lastUpdTime && <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: 3 }}>{lastUpdTime}</span>}
          </div>
        )}
        <button onClick={handleRefresh} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
          padding: '3px 9px', cursor: 'pointer',
          color: 'rgba(255,255,255,0.45)', fontSize: 8.5,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          fontFamily: T.FONT,
        }}>
          <RefreshCw size={8} /> Refresh
        </button>
      </div>
    </div>
  );

  // ── Scan log ──────────────────────────────────────────────────────────────

  const scanLog = (
    <>
      {hasActivities && (
        <div style={{ borderTop: `1px solid ${T.GREY_2}` }}>
          <button onClick={() => setShowLog(v => !v)} style={{
            width: '100%', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', padding: '12px 20px',
            background: '#fafafa', border: 'none', cursor: 'pointer',
            fontSize: 8.5, fontWeight: 700, color: T.GREY_5,
            letterSpacing: '0.3em', textTransform: 'uppercase',
            fontFamily: T.FONT,
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={10} strokeWidth={2.5} />
              Scan History&nbsp;({activities.length})
            </span>
            {showLog ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {showLog && (
            <div style={{ padding: '0 20px 16px', maxHeight: 280, overflowY: 'auto' }}>
              {activities.map((a, i) => <LogRow key={i} activity={a} isLatest={i === 0} />)}
            </div>
          )}
        </div>
      )}
      {!hasActivities && (
        <div style={{
          borderTop: `1px solid ${T.GREY_2}`, padding: '10px 20px',
          fontSize: 10, color: T.GREY_3, textAlign: 'center',
          fontFamily: T.FONT, letterSpacing: '0.05em',
        }}>
          Scan history will appear once the shipment is in transit
        </div>
      )}
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{TT_STYLES}</style>
      <div style={{
        background: T.WHITE,
        border: `1.5px solid ${T.GREY_2}`,
        borderRadius: 4,      // sharp VESHH-EON corners
        overflow: 'hidden',
        fontFamily: T.FONT,
        WebkitFontSmoothing: 'antialiased',
      }}>
        {header}

        {isDesktop ? (
          <DesktopLayout
            currentStageIdx={curIdx}
            activities={activities}
            orderDates={orderDates}
            displayLeftPct={displayLeftPct}
            animating={animating}
          />
        ) : (
          <MobileLayout
            currentStageIdx={curIdx}
            activities={activities}
            orderDates={orderDates}
            vFillH={vFillH}
            displayTopPx={displayTopPx}
            animating={animating}
          />
        )}

        {/* Stale notice — shown when last scan >1 day ago */}
        <StaleNotice lastUpdated={trackingData?.lastUpdated} currentStageKey={currentStageKey} />

        {scanLog}
      </div>
    </>
  );
}
